import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  Popover,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GradeIcon from "@mui/icons-material/Grade";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { Chart, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { toast, ToastContainer } from "react-toastify";
import { listCourses, listAllSubjects } from "../../api/assessmentHierarchy";
import { listTests } from "../../api/assessmentTests";
import { getReports, downloadReportsCsv, apiErrorMessage } from "../../api/assessmentReports";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
);

const ACCENT = "#17479e";

// Shared card shadow so the graph cards read as part of the same set as the
// stat cards below.
const CARD_SHADOW = "0 6px 20px rgba(23,71,158,0.08), 0 1px 3px rgba(0,0,0,0.04)";

// Matches the "glass card" look used in AssessmentDashboard.jsx / MentorPayments.jsx.
const cardStyle = {
  borderRadius: 3,
  background: "rgba(255,255,255,0.78)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.6)",
};

// Rounded, borderless "pill" look for the filter dropdowns.
const pillSelectSx = {
  minWidth: 150,
  backgroundColor: "#fff",
  borderRadius: "12px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e7ee" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#c7cce0" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
};

const SLICE_COLORS = ["#5c5ce6", "#3f8efc", "#37c26d", "#ff8a3d", "#ec4899", "#14b8a6", "#f59e0b", "#8b5cf6"];

const formatRupees = (value) =>
  `\u20B9${Number(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Stat card defs — same recipe as AssessmentDashboard.jsx's STAT_DEFS
// (icon, iconBg gradient, borderColor + glowColor for the card shadow/top-border).
const statCards = (d) => [
  {
    key: "avgScore",
    label: "Avg Score",
    value: `${Math.round((d?.avgScorePercentage ?? 0) * 10) / 10}%`,
    icon: <GradeIcon sx={{ fontSize: 18, color: "#7c4dff" }} />,
    iconBg: "linear-gradient(135deg, #f3f0ff, #d1c4e9)",
    borderColor: "#9575cd",
    glowColor: "rgba(149,117,205,0.2)",
  },
  {
    key: "attempts",
    label: "Total Attempts",
    value: (d?.totalAttempts ?? 0).toLocaleString("en-IN"),
    icon: <DescriptionIcon sx={{ fontSize: 18, color: "#42a5f5" }} />,
    iconBg: "linear-gradient(135deg, #f0f7ff, #bbdefb)",
    borderColor: "#64b5f6",
    glowColor: "rgba(100,181,246,0.2)",
  },
  {
    key: "completion",
    label: "Completion Rate",
    value: `${Math.round((d?.completionRate ?? 0) * 10) / 10}%`,
    icon: <CheckCircleIcon sx={{ fontSize: 18, color: "#66bb6a" }} />,
    iconBg: "linear-gradient(135deg, #f1f8f1, #dcedc8)",
    borderColor: "#81c784",
    glowColor: "rgba(129,199,132,0.2)",
  },
  {
    key: "revenue",
    label: "Revenue",
    value: formatRupees(d?.totalRevenue),
    icon: <CreditCardIcon sx={{ fontSize: 18, color: "#ff9800" }} />,
    iconBg: "linear-gradient(135deg, #fff8f0, #ffe0b2)",
    borderColor: "#ffb74d",
    glowColor: "rgba(255,183,77,0.2)",
  },
];

function StatCard({ icon, iconBg, borderColor, glowColor, value, label }) {
  return (
    <Card
      sx={{
        ...cardStyle,
        boxShadow: `0 2px 14px ${glowColor}, 0 1px 3px rgba(0,0,0,0.05)`,
        borderTop: `3px solid ${borderColor}`,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": { transform: "translateY(-2px)" },
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box
            sx={{
              background: iconBg,
              borderRadius: 1.5,
              width: 34,
              height: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontSize: "0.72rem" }}>
            {label}
          </Typography>
        </Box>

        <Typography sx={{ fontWeight: 800, fontSize: "1.3rem", color: "#1a1a2e" }}>{value}</Typography>

      </CardContent>
    </Card>
  );
}

// Bucket size is chosen by the backend from the window length; infer it from the
// spacing of the first two points so the x-axis labels fit the granularity.
const formatPeriod = (periodStart, spacingDays) => {
  const d = new Date(`${periodStart}T00:00:00`);
  if (spacingDays >= 28) return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const trendData = (trend = []) => {
  const spacing =
    trend.length > 1
      ? Math.round((new Date(trend[1].periodStart) - new Date(trend[0].periodStart)) / 86400000)
      : 1;
  return {
    labels: trend.map((t) => formatPeriod(t.periodStart, spacing)),
    datasets: [
      {
        type: "bar",
        label: "Attempts",
        data: trend.map((t) => t.attemptCount),
        backgroundColor: "#e3e2fb",
        borderRadius: 4,
        yAxisID: "y1",
        order: 2,
      },
      {
        type: "line",
        label: "Avg Score %",
        data: trend.map((t) => Math.round(t.avgScorePercentage * 10) / 10),
        borderColor: "#5c5ce6",
        backgroundColor: "#5c5ce6",
        borderWidth: 2.5,
        pointBackgroundColor: "#5c5ce6",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        tension: 0.35,
        yAxisID: "y",
        order: 1,
      },
    ],
  };
};

const trendOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: { usePointStyle: true, font: { size: 12 } },
    },
    tooltip: {
      backgroundColor: "rgba(255,255,255,0.95)",
      titleColor: "#1a1a2e",
      bodyColor: "#555",
      borderColor: "#e0e0e0",
      borderWidth: 1,
      padding: 10,
    },
  },
  scales: {
    x: { grid: { display: false } },
    y: {
      position: "left",
      beginAtZero: true,
      max: 100,
      grid: { color: "#f0f0f0" },
      ticks: { color: "#9e9e9e", font: { size: 12 } },
    },
    y1: {
      position: "right",
      beginAtZero: true,
      grid: { display: false },
      ticks: { color: "#9e9e9e", font: { size: 12 }, precision: 0 },
    },
  },
};

const pieData = (labels, values) => ({
  labels,
  datasets: [
    {
      data: values,
      backgroundColor: labels.map((_, i) => SLICE_COLORS[i % SLICE_COLORS.length]),
      borderColor: "#fff",
      borderWidth: 3,
      hoverOffset: 8,
    },
  ],
});

const pieOptions = (formatValue) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: { usePointStyle: true, font: { size: 12 } },
    },
    tooltip: {
      backgroundColor: "rgba(255,255,255,0.95)",
      titleColor: "#1a1a2e",
      bodyColor: "#555",
      borderColor: "#e0e0e0",
      borderWidth: 1,
      padding: 10,
      callbacks: { label: (ctx) => `${ctx.label}: ${formatValue(ctx.parsed)}` },
    },
  },
});

const DATE_FILTER_OPTIONS = [
  { label: "Last 1 Month", value: "1M" },
  { label: "Last 3 Months", value: "3M" },
  { label: "Last 6 Months", value: "6M" },
  { label: "Last 1 Year", value: "1Y" },
];

const dateFilterLabel = (activeFilter, startDate, endDate) => {
  if (activeFilter === "custom" && startDate && endDate) return `${startDate} – ${endDate}`;
  return DATE_FILTER_OPTIONS.find((o) => o.value === activeFilter)?.label ?? "Last 3 Months";
};

const PRESET_MONTHS = { "1M": 1, "3M": 3, "6M": 6, "1Y": 12 };

// Resolves the picked date filter into the ISO date-times the API expects. Presets
// are "now minus N months .. now"; a custom range runs from the start of the first
// day to the start of the day after the last one, since the backend window is
// half-open [start, end).
const resolveWindow = (activeFilter, startDate, endDate) => {
  if (activeFilter === "custom" && startDate && endDate) {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    end.setDate(end.getDate() + 1);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }
  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - (PRESET_MONTHS[activeFilter] ?? 3));
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};

function AssessmentReports() {
  const [courses, setCourses] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [tests, setTests] = useState([]);
  const [courseFilter, setCourseFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [testFilter, setTestFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("3M");
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const filterLabel = dateFilterLabel(activeFilter, startDate, endDate);

  const handleFilterChange = (val) => {
    setActiveFilter(val);
    setAnchorEl(null);
  };

  // Custom range applies the moment both ends are picked — no separate
  // Apply/Reset step needed.
  useEffect(() => {
    if (startDate && endDate) setActiveFilter("custom");
  }, [startDate, endDate]);

  // Filter option lists.
  useEffect(() => {
    Promise.all([listCourses({ includeInactive: true }), listAllSubjects()])
      .then(([courseRows, subjectRows]) => {
        setCourses(courseRows);
        setAllSubjects(subjectRows);
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load filters")));
  }, []);

  // Tests follow the selected course (all tests when no course is chosen).
  useEffect(() => {
    listTests({ courseId: courseFilter || undefined, size: 100 })
      .then((data) => setTests(data.content || []))
      .catch(() => setTests([]));
  }, [courseFilter]);

  const subjectOptions = useMemo(
    () => allSubjects.filter((s) => !courseFilter || s.courseIds?.includes(courseFilter)),
    [allSubjects, courseFilter],
  );

  const handleCourseChange = (value) => {
    setCourseFilter(value);
    setSubjectFilter("");
    setTestFilter("");
  };

  const queryParams = () => ({
    courseId: courseFilter,
    subjectId: subjectFilter,
    testId: testFilter,
    ...resolveWindow(activeFilter, startDate, endDate),
  });

  // The "custom" filter only takes effect once both dates are set, which the
  // effect above guarantees, so every change of these inputs is a valid query.
  useEffect(() => {
    setLoading(true);
    getReports(queryParams())
      .then(setReport)
      .catch((err) => {
        setReport(null);
        toast.error(apiErrorMessage(err, "Failed to load report"));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseFilter, subjectFilter, testFilter, activeFilter, startDate, endDate]);

  const handleExport = () => {
    setExporting(true);
    downloadReportsCsv(queryParams())
      .then(() => toast.success("Report exported."))
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to export report")))
      .finally(() => setExporting(false));
  };

  const breakdownLabel = report?.breakdownBy === "SUBJECT" ? "Subject" : "Exam";
  const pieSlices = report?.pieSlices ?? [];
  const revenueSlices = report?.revenueByCourse ?? [];

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ borderRadius: 3, boxShadow: CARD_SHADOW, border: "1px solid #f0f1f6", mb: 2.5 }}>
        <CardContent>
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
              <Select
                displayEmpty
                size="small"
                value={courseFilter}
                onChange={(e) => handleCourseChange(e.target.value)}
                renderValue={(selected) => courses.find((c) => c.id === selected)?.name || "Exam"}
                sx={pillSelectSx}
              >
                <MenuItem value="">All Exams</MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
              <Select
                displayEmpty
                size="small"
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                renderValue={(selected) => subjectOptions.find((x) => x.id === selected)?.name || "Subject"}
                sx={{ ...pillSelectSx, minWidth: 170 }}
              >
                <MenuItem value="">All Subjects</MenuItem>
                {subjectOptions.map((x) => (
                  <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>
                ))}
              </Select>
              <Select
                displayEmpty
                size="small"
                value={testFilter}
                onChange={(e) => setTestFilter(e.target.value)}
                renderValue={(selected) => tests.find((t) => t.id === selected)?.name || "Test"}
                sx={{ ...pillSelectSx, minWidth: 170 }}
              >
                <MenuItem value="">All Tests</MenuItem>
                {tests.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </Box>

            {/* Date range + Export, grouped on the right */}
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
            <Box>
              <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  cursor: "pointer",
                  background: "rgba(255,255,255,0.85)",
                  border: "1px solid #e8eaf0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  transition: "all 0.2s",
                  "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.1)", borderColor: "#bdbdbd" },
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 16, color: ACCENT }} />
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#444" }}>
                  {filterLabel}
                </Typography>
                <Box sx={{ fontSize: 10, color: "#aaa", ml: 0.5 }}>▼</Box>
              </Box>

              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 3,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.13)",
                    minWidth: 220,
                    overflow: "hidden",
                    border: "1px solid #f0f0f0",
                  },
                }}
              >
                {DATE_FILTER_OPTIONS.map((opt) => (
                  <Box
                    key={opt.value}
                    onClick={() => handleFilterChange(opt.value)}
                    sx={{
                      px: 2.5,
                      py: 1.2,
                      cursor: "pointer",
                      fontSize: "0.83rem",
                      fontWeight: activeFilter === opt.value ? 700 : 500,
                      color: activeFilter === opt.value ? ACCENT : "#444",
                      background: activeFilter === opt.value ? "rgba(23,71,158,0.07)" : "#fff",
                      borderLeft: activeFilter === opt.value ? `3px solid ${ACCENT}` : "3px solid transparent",
                      transition: "all 0.15s",
                      "&:hover": { background: "rgba(23,71,158,0.06)", color: ACCENT },
                    }}
                  >
                    {opt.label}
                  </Box>
                ))}
                <Box sx={{ height: "1px", background: "#f0f0f0", mx: 2 }} />
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="#aaa"
                    sx={{ textTransform: "uppercase", letterSpacing: 1, fontSize: "0.68rem" }}
                  >
                    Custom Range
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mt: 1 }}>
                    <TextField
                      label="Start Date"
                      type="date"
                      size="small"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.82rem" } }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      size="small"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: startDate }}
                      fullWidth
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.82rem" } }}
                    />
                  </Box>
                </Box>
              </Popover>
            </Box>

            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
              disableElevation
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                backgroundColor: "#eef0f5",
                color: "#444",
                "&:hover": { backgroundColor: "#e2e5ee" },
              }}
            >
              Export
            </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {loading && !report ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
              gap: 1.75,
              mb: 2.5,
            }}
          >
            {statCards(report).map((card) => (
              <StatCard key={card.key} {...card} />
            ))}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.6fr 1fr" },
              gap: 2.5,
              mb: 2.5,
            }}
          >
            <Card sx={{ borderRadius: 3, boxShadow: CARD_SHADOW, border: "1px solid #f0f1f6" }}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 2 }}>
                  Score &amp; Engagement Trend
                </Typography>
                <Box sx={{ height: 300 }}>
                  <Chart type="bar" data={trendData(report?.trend)} options={trendOptions} />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, boxShadow: CARD_SHADOW, border: "1px solid #f0f1f6" }}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 2 }}>
                  Attempts by {breakdownLabel}
                </Typography>
                {pieSlices.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 8, textAlign: "center" }}>
                    No attempts in this period
                  </Typography>
                ) : (
                  <Box sx={{ height: 260 }}>
                    <Pie
                      data={pieData(pieSlices.map((x) => x.label), pieSlices.map((x) => x.percentage))}
                      options={pieOptions((v) => `${Math.round(v * 10) / 10}%`)}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* The backend omits revenue-by-course once a single course is selected. */}
          {report?.revenueByCourse != null && (
            <Card sx={{ borderRadius: 3, boxShadow: CARD_SHADOW, border: "1px solid #f0f1f6", maxWidth: 520 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 2 }}>
                  Revenue by Exam
                </Typography>
                {revenueSlices.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 8, textAlign: "center" }}>
                    No revenue in this period
                  </Typography>
                ) : (
                  <Box sx={{ height: 260 }}>
                    <Pie
                      data={pieData(revenueSlices.map((x) => x.label), revenueSlices.map((x) => x.amount))}
                      options={pieOptions((v) => formatRupees(v))}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
}

export default AssessmentReports;
